const { ConsentString } = require('consent-string');
const Cookies = require('js-cookie');
import { CMPCommands } from '../Enums/CMPCommands.enums';
import Constants from '../constants';
import { UtilityService } from './utility.service';
import 'whatwg-fetch';

declare interface cmpCallback {
    (object: any, success: boolean): void;
}

declare let __cmp: any;

export default class CmpJSApi {
    /**
     * each call consists of ['action', 'parameter', 'callback']
     */
    public static callStack: [string, (number[] | string | number | null), cmpCallback][] = [];

    static SERVICE_PATH_URL = {
        purposeListTranslated: '//vendorlist.consensu.org/purposes-{language}.json',
        vendorListLatest: '//vendorlist.consensu.org/vendorlist.json',
        vendorListVersioned: '//vendorlist.consensu.org/v-{version}/vendorlist.json',
        localFile: '../../mocks/tcfVendorList.json'
    };

    private _utilityService: UtilityService;

    public static explicitConsentGiven = false;

    get utilityService() {
        return this._utilityService;
    }

    set utilityService(utilityService: UtilityService) {
        this._utilityService = utilityService;
    }

    constructor(utilityService: UtilityService) {
        this.utilityService = utilityService;
        if (CmpJSApi.getConsentCookieValue() !== null) {
            CmpJSApi.signalExplicitConsentGiven();
        }
    }

    /**
     * When an explicit consent was given, remember this state
     * and execute any stored calls
     */
    static signalExplicitConsentGiven() {
        CmpJSApi.explicitConsentGiven = true;
        CmpJSApi.processCallStack();
    }

    /**
     * This function is used to turn on CMP JS Api functionality
     */
    turnOnCmpJSApi() {
        // Takeover existing callstack from stub, if existing
        if (typeof window[Constants.CMP_STRING] === 'function') {
            // Calling "__cmp()" without arguments should return the callstack
            const existingCalls = window[Constants.CMP_STRING]();

            // Check if we have at least one callback waiting
            if (Array.isArray(existingCalls) && existingCalls.length > 0) {
                CmpJSApi.callStack = existingCalls;
            }
        }

        //TODO need to be moved in window usercentrics service
        window[Constants.USERCENTRICS_STRING].getEncodedString = function () {
            return CmpJSApi.getConsentCookieValue();
        };

        window[Constants.USERCENTRICS_STRING].getConsentStringObject = function () {
            return new ConsentString(CmpJSApi.getConsentCookieValue());
        };

        window[Constants.CMP_STRING] = this.cmpWindowFunction;

        // Check if we need to process any existing calls
        CmpJSApi.processCallStack();
    }

    /**
     * Takes all calls stored in the callstack and executes them in the order they were stored.
     */
    static processCallStack = () => {
        // Don't process if no explicit consent was given.
        if (!CmpJSApi.explicitConsentGiven) {
            return;
        }

        while (CmpJSApi.callStack.length > 0) {
            const storedCall = CmpJSApi.callStack.shift();
            window[Constants.CMP_STRING](storedCall[0], storedCall[1], storedCall[2]);
        }
    };

    cmpWindowFunction = (command?: string, parameter?: number[] | string | number, callback?: cmpCallback) => {
        /*
         * If function was called without elements, return the callstack
         * (this is part of the stub specification)
         */
        if (typeof command === 'undefined' && typeof parameter === 'undefined' && typeof callback === 'undefined') {
            return CmpJSApi.callStack;
        }

        // if we have a command, we require a callback
        if (typeof command !== 'undefined' && typeof callback !== 'function') {
            throw new Error('Callback missing for call with command ' + command);
        }

        // If no explicit was given, store this calls
        if (!CmpJSApi.explicitConsentGiven) {
            CmpJSApi.callStack.push([command, parameter, callback]);
            return true;
        }

        this._utilityService.developmentLog('Executing call for ' + command);

        try {
            switch (command) {
                case CMPCommands.GET_CONSENT_DATA:
                    callback(CmpJSApi.getConsentDataString(parameter), true);
                    break;

                case CMPCommands.GET_VENDOR_CONSENTS: {
                    // If null is passed, transform to empty array for easier type checking
                    if (parameter === null) {
                        parameter = [];
                    }

                    // Make sure parameter is an array
                    if (!Array.isArray(parameter)) {
                        throw new Error('vendorIds are not an array');
                    }
                    const vendorConsentsData = this.getVendorConsentsData(parameter);
                    callback(vendorConsentsData, true);
                    break;
                }

                case CMPCommands.GET_PUBLISHER_CONSENTS: {
                    if (parameter === null) {
                        parameter = [];
                    }
                    // Make sure parameter is an array
                    if (!Array.isArray(parameter)) {
                        throw new Error('vendorIds are not an array');
                    }
                    const publisherConsentsData = this.getPublisherConsentsData(parameter);
                    callback(publisherConsentsData, true);
                    break;
                }

                case CMPCommands.GET_VENDOR_LIST: {

                    if (parameter !== null && typeof parameter !== 'string' && typeof parameter !== 'number') {
                        throw new Error('vendorListVersion is neither string nor empty');
                    }

                    this.getVendorList(parameter).then((vendorList) => {
                        // eslint-disable-next-line promise/no-callback-in-promise
                        callback(vendorList, true);
                        return;
                    }).catch(() => {
                        // eslint-disable-next-line promise/no-callback-in-promise
                        callback(null, false);
                    });

                    break;
                }

                case CMPCommands.PING:
                    callback(CmpJSApi.getPingReturn(), true);
                    break;

                default:
                    return false;
            }
        } catch (error) {
            callback(null, false);
            return false;
        }

        return true;
    };

    /**
     * This function is used to turn off cmp js api functionality.
     */
    static turnOffCmpJSApi() {
        window[Constants.USERCENTRICS_STRING].getEncodedString = null;
        window[Constants.USERCENTRICS_STRING].getConsentStringObject = null;
        // Reinstall the stub
        window[Constants.CMP_STRING] = CmpJSApi.stubCMP;

        // Pass stored callback if any
        if (CmpJSApi.callStack.length !== 0) {
            window[Constants.CMP_STRING].a = CmpJSApi.callStack;
        }
    }

    public getTranslatedPurpose(language: string): any {
        return fetch(CmpJSApi.SERVICE_PATH_URL.localFile).then(response => response.json());
    }

    /**
     * Fetches the vendorlist in a version specified by IAB documentation
     * @param version 
     */
    public getVendorList(version: string | number | number[] | null): Promise<Object> {
        let url: string = CmpJSApi.SERVICE_PATH_URL.vendorListLatest;

        if (version === null) {
            const cookieValue = CmpJSApi.getConsentCookieValue();
            if (cookieValue !== null) {
                const consentString = new ConsentString(cookieValue);
                const decodedMetadata = ConsentString.decodeMetadataString(consentString.getMetadataString());
                version = decodedMetadata.vendorListVersion;
            }
        }

        if (version !== null && version !== 'LATEST') {
            url = CmpJSApi.SERVICE_PATH_URL.vendorListVersioned.replace('{version}', version.toString());
        }

        return fetch(CmpJSApi.SERVICE_PATH_URL.localFile).then(response => {
            return response.json();
        });
    }

    /**
     * Official stub template from
     * https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/...
     *  ...blob/master/CMP%20JS%20API%20v1.1%20Final.md#CMP-stub-sample
     */
    public static stubCMP() {
        const b = arguments;
        __cmp.a = __cmp.a || [];
        if (!b.length) {
            return __cmp.a;
        } else if (b[0] === 'ping') {
            b[2]({ gdprAppliesGlobally: false, cmpLoaded: false }, true);
        } else {
            __cmp.a.push([].slice.apply(b));
        }
        return true;
    }

    static getPingReturn() {
        return {
            gdprAppliesGlobally: true,
            cmpLoaded: true
        };
    }

    static getConsentDataString(consentStringVersion) {
        try {
            const consentStringDecoded = new ConsentString(CmpJSApi.getConsentCookieValue());

            const consentStringToBeReturned = CmpJSApi.buildConsentString(
                CmpJSApi.getConsentCookieValue(),
                consentStringDecoded,
                consentStringVersion
            );

            if (consentStringToBeReturned) {
                return {
                    gdprApplies: CmpJSApi.gdprApplies(),
                    hasGlobalScope: false,
                    consentData: consentStringToBeReturned
                };
            }
            return null;
        } catch {
            return null;
        }
    }

    static buildConsentString(consentStringEncoded: string, consentStringDecoded, consentStringVersionString) {
        const consentStringVersion = consentStringVersionString
            ? parseInt(consentStringVersionString, 10)
            : Constants.LATEST_CONSENT_STRING_VERSION;
        return !isNaN(consentStringVersion) && consentStringVersion <= consentStringDecoded.getVersion()
            ? consentStringEncoded
            : null;
    }

    /**
     * Returns the PublisherConsents based on the purposeIds passed
     * @param purposeIds Array of purposeIds. If empty, this means all purposes
     */
    getPublisherConsentsData(purposeIds: number[]): IAB.PublisherConsents {
        const consentString = new ConsentString(CmpJSApi.getConsentCookieValue());

        // If the passed purposeIds are empty, this means all purposes
        if (purposeIds.length === 0) {
            purposeIds = this.getPurposes();
        }

        // Fetch all valid purposes and add those that were passed but are not valid
        const allPurposeIds = this.getPurposes();
        purposeIds.forEach(purposeId => {
            // Skip invalid purposeIds
            if (purposeId < 1 || purposeId > 88) {
                return;
            }
            if (allPurposeIds.indexOf(purposeId) === -1) {
                allPurposeIds.push(purposeId);
            }
        });

        // We need to split the all purposeIds up into two sections: 1-24 and 25-88 (according to docs)
        const standardPurposeIds = allPurposeIds.filter(
            purposeId => purposeIds.indexOf(purposeId) !== -1 && purposeId <= 24
        );
        const customPurposeIds = allPurposeIds.filter(
            purposeId => purposeIds.indexOf(purposeId) !== -1 && purposeId > 24
        );

        // An object with all purposes and their status e.g. {'2': true, '3': false}
        const standardPurposes = CmpJSApi.buildPurposeConsents(consentString.getPurposesAllowed(), standardPurposeIds);
        const customPurposes = CmpJSApi.buildPurposeConsents(consentString.getPurposesAllowed(), customPurposeIds);

        return {
            metadata: consentString.getMetadataString(),
            gdprApplies: CmpJSApi.gdprApplies(),
            hasGlobalScope: false,
            standardPurposeConsents: standardPurposes,
            customPurposeConsents: customPurposes
        };
    }

    getVendorConsentsData(vendorIds: number[]): IAB.VendorConsents {
        const consentString = new ConsentString(CmpJSApi.getConsentCookieValue());
        return {
            metadata: consentString.getMetadataString(),
            gdprApplies: CmpJSApi.gdprApplies(),
            hasGlobalScope: false,
            purposeConsents: CmpJSApi.buildPurposeConsents(consentString.getPurposesAllowed(), this.getPurposes()),
            vendorConsents: this.buildVendorConsents(consentString, vendorIds)
        };
    }

    buildVendorConsents(consentString, requestedVendorIds) {
        const vendorIds = requestedVendorIds && requestedVendorIds.length ? requestedVendorIds : this.getVendorIds();
        const allowedVendors = consentString.getVendorsAllowed();
        return vendorIds.reduce((map, vendorId) => {
            map[vendorId] = allowedVendors.indexOf(vendorId) !== -1;
            return map;
        }, {});
    }

    getVendorIds(): number[] {
        return this.utilityService.cmpIABService.vendors.map(vendor => vendor.id);
    }

    getPurposes(): number[] {
        return this.utilityService.cmpIABService.purposes.map(purpose => purpose.id);
    }

    static getConsentCookieValue(): any {
        const cookieValue = Cookies.get(Constants.CMP_IAB_COOKIE_NAME_FIRST_PARTY);
        if (typeof cookieValue !== 'undefined') {
            return cookieValue;
        }
        if (
            typeof window.globalConsentCookie !== 'undefined' &&
            typeof window.globalConsentCookie.cookieValue !== 'undefined'
        ) {
            return window.globalConsentCookie.cookieValue;
        }
        return null;
    }

    static gdprApplies() {
        return true;
    }

    /**
     * Returns an object with the purposeId as key and boolean true/false as value
     * signaling if the purpose is allowed or not.
     *
     * @param allowedPurposeIds
     * @param allPurposeIds
     * @param requestedPurposeIds
     */
    static buildPurposeConsents(allowedPurposeIds: number[], allPurposeIds: number[]) {
        return allPurposeIds
            .filter(purposeId => allPurposeIds.indexOf(purposeId) !== -1)
            .reduce((map, purposeId) => {
                map[purposeId] = allowedPurposeIds.indexOf(purposeId) !== -1;
                return map;
            }, {});
    }
}
